import { ApiTagTypes, innovorderApi } from '../index';
import { ApiRequest, ApiResponse } from '../request.types';
import {
    GetRestaurantDetailsPayload,
    Restaurant,
    RestaurantPayload,
    RestaurantModule,
    RestaurantOptions,
    UpdateRestaurantOptionsPayload,
    GetRestaurantShiftsResponse,
    GetRestaurantShiftsPayload,
    GetRestaurantListV2Payload,
    GetRestaurantListV2Response,
    UpdateRestaurantPayload,
    LegalInfo,
    DeleteRestaurantPayload,
    GetPickupSchedulesResponse,
    SavePickupSchedulesPayload,
} from './restaurant.type';

const restaurantApi = innovorderApi.injectEndpoints({
    endpoints: (build) => ({
        getRestaurants: build.query<Restaurant[], Record<string, never> | void>({
            query: () => ({
                url: `/restaurants`,
                method: 'GET',
            }),
            transformResponse: (response: ApiResponse<Restaurant[]>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        getRestaurantListV2: build.query<GetRestaurantListV2Response, ApiRequest<GetRestaurantListV2Payload>>({
            query: (data) => {
                const { offset, limit, search, filter, sortBy, sortDirection, token } = data;
                return {
                    url: `restaurants/v2/list`,
                    method: 'GET',
                    params: { offset, limit, search, ...filter, sortBy, sortDirection },
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                };
            },
            transformResponse: (response: ApiResponse<GetRestaurantListV2Response>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        getRestaurantDetails: build.query<RestaurantPayload, ApiRequest<GetRestaurantDetailsPayload>>({
            query: ({ restaurantId, token }) => ({
                url: `/restaurants/${restaurantId}`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<RestaurantPayload>) => response.data,
            providesTags: [ApiTagTypes.Restaurant],
            extraOptions: { maxRetries: 3 },
        }),

        getRestaurantModules: build.query<RestaurantModule, ApiRequest<GetRestaurantDetailsPayload>>({
            query: ({ restaurantId, token }) => ({
                url: `/restaurants/${restaurantId}/modules`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<RestaurantModule>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        getRestaurantShifts: build.query<GetRestaurantShiftsResponse, ApiRequest<GetRestaurantShiftsPayload>>({
            query: ({ restaurantId, token }) => ({
                url: `/restaurants/${restaurantId}/shifts`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<GetRestaurantShiftsResponse>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        kioskReload: build.mutation({
            query: ({ restaurantId }) => ({
                url: `/restaurants/kiosk/reload?restaurantId=${restaurantId}`,
                method: 'POST',
                body: { restaurantId },
            }),
            extraOptions: { maxRetries: 3 },
        }),

        updateRestaurantOptions: build.mutation<RestaurantOptions, ApiRequest<UpdateRestaurantOptionsPayload>>({
            query: ({ restaurantId, token, options }) => ({
                url: `/restaurants/${restaurantId}/options`,
                method: 'PUT',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: {
                    restaurantId,
                    options,
                },
            }),
            transformResponse: (response: ApiResponse<RestaurantOptions>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        updateRestaurant: build.mutation<RestaurantPayload, ApiRequest<UpdateRestaurantPayload>>({
            query: ({ token, brandId, restaurantId, ...body }) => ({
                url: `/brand/${brandId}/restaurants/${restaurantId}`,
                method: 'PUT',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: { brandId, restaurantId, ...body },
            }),
            transformResponse: (response: ApiResponse<RestaurantPayload>) => response.data,
            invalidatesTags: [ApiTagTypes.Restaurant],
        }),

        getRestaurantLegal: build.query<LegalInfo, ApiRequest<{ restaurantId: string }>>({
            query: ({ restaurantId, token }) => ({
                url: `/restaurants/${restaurantId}/legal`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<LegalInfo>) => response.data,
            providesTags: [ApiTagTypes.OperationZone],
        }),

        updateRestaurantLegal: build.mutation<LegalInfo, ApiRequest<{ restaurantId: string } & LegalInfo>>({
            query: ({ restaurantId, token, ...body }) => ({
                url: `/restaurants/${restaurantId}/legal`,
                method: 'PUT',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body,
            }),
            transformResponse: (response: ApiResponse<LegalInfo>) => response.data,
            // Invalidates OperationZone so the operation zone modal reflects edits made
            // from the restaurant legal section (they share the same Legal row).
            invalidatesTags: [ApiTagTypes.OperationZone],
        }),

        deleteRestaurant: build.mutation<void, ApiRequest<DeleteRestaurantPayload>>({
            query: ({ restaurantId, token }) => ({
                url: `/restaurants/${restaurantId}`,
                method: 'DELETE',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
        }),

        getPickupSchedules: build.query<GetPickupSchedulesResponse, ApiRequest<{ restaurantId: string }>>({
            query: ({ restaurantId, token }) => ({
                url: `/restaurants/${restaurantId}/pickup_schedules`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<GetPickupSchedulesResponse>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        savePickupSchedules: build.mutation<GetPickupSchedulesResponse, ApiRequest<SavePickupSchedulesPayload>>({
            query: ({ restaurantId, token, pickupSchedules }) => ({
                url: `/restaurants/${restaurantId}/pickup_schedules`,
                method: 'POST',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: { pickupSchedules },
            }),
            transformResponse: (response: ApiResponse<GetPickupSchedulesResponse>) => response.data,
        }),
    }),
});

export const {
    useGetRestaurantsQuery,
    useGetRestaurantModulesQuery,
    useKioskReloadMutation,
    useGetRestaurantShiftsQuery,
    useGetRestaurantDetailsQuery,
    useUpdateRestaurantOptionsMutation,
    useLazyGetRestaurantListV2Query,
    useUpdateRestaurantMutation,
    useGetRestaurantLegalQuery,
    useUpdateRestaurantLegalMutation,
    useDeleteRestaurantMutation,
    useGetPickupSchedulesQuery,
    useSavePickupSchedulesMutation,
} = restaurantApi;
